class PredictiveSearch extends HTMLElement {
  static MIN_QUERY_LENGTH = 2;

  constructor() {
    super();
    this.cachedMap = new Map();
    this.abortController = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    // MAHA (2026-08-25): cierre por scroll. El buscador ahora puede quedar
    // pegado dentro de una cabecera sticky -- antes, con la cabecera
    // yendose con el scroll, el desplegable de resultados desaparecia solo
    // de la vista. Mismo patron que boundHandleClickOutside: un listener
    // conectado desde el constructor, que solo actua si el estado esta
    // abierto -- no un mecanismo nuevo.
    this.scrollYAtOpen = null;
    this.boundHandleScrollClose = this.handleScrollClose.bind(this);
    document.addEventListener('scroll', this.boundHandleScrollClose, { capture: true, passive: true });
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    // MAHA (2026-09-02): el alcance tambien al ENVIAR el formulario.
    // El <select> de categorias no tiene atributo name, asi que nunca viajaba en
    // el submit: elegir "Platos" y pulsar Enter llevaba a /search?q=plato, sin
    // alcance ninguno. /search solo entiende `q`, de modo que la unica via es
    // componerla igual que en setupURL(). Sin categoria elegida NO se toca nada:
    // el envio sigue siendo el nativo del navegador, byte por byte como hoy.
    this.searchForm = this.querySelector('form[role="search"]');
    this.searchForm?.addEventListener('submit', this.handleSubmit.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    document.addEventListener('menu-drawer:open', () => {
      this.classList.remove('predictive-search-open');
      document.body.classList.remove('search-open');
    });

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom)}px`
    );
    this.scrollYAtOpen = window.scrollY;
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
      this.toggleNavButtonVisibility(false);
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent.classList.remove('hidden');
    }
    this.classList.add('predictive-search-open');
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) {
        this.searchContent.classList.add('hidden');
      }
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.abortController?.abort();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
      return;
    }
    // MAHA (2026-09-17): caja vacia + categoria elegida. Antes de esto no pasaba
    // nada -- ni en escritorio ni en celular -- porque el resto del flujo solo
    // compone el alcance al ENVIAR el formulario (handleSubmit), y sin texto no
    // hay submit que dispare. Aqui se navega directo a la coleccion real
    // (data-url en el <option>, ver predictive-search.liquid) en vez del
    // `tag:` sintetico, que da resultados de busqueda y no la coleccion.
    const selected = this.searchProductTypes?.selectedOptions?.[0];
    const collectionUrl = selected?.dataset.url;
    if (collectionUrl) {
      window.location.assign(collectionUrl);
    }
  }

  handleSubmit(evt) {
    const scope = this.searchProductTypes ? this.searchProductTypes.value : '';
    // Sin categoria: dejar pasar el envio nativo tal cual (mismo comportamiento
    // que antes de este cambio). Solo se interviene cuando hay alcance que llevar.
    if (!scope) {
      // MAHA (2026-09-17): si NO hay categoria elegida a mano, pero lo que se
      // tecleo casa EXACTO (normalizado, tolerando una "s" final) con una
      // categoria del mismo mapa que arma el <select>, mandar a la coleccion
      // real en vez de a /search -- ahi el buscador nativo se vacia despues de
      // 3-5 paginas aunque existan cientos de productos (medido en
      // BUSCADOR-CUANTOS-SIRVE-DE-VERDAD). Coincidencia EXACTA solamente: nada
      // de sinonimos, nada de "contiene". Si no casa, sigue igual que hoy.
      const term = this.getQuery();
      const collectionUrl = term ? PredictiveSearch.matchCategoryUrl(term, this.searchProductTypes) : null;
      if (collectionUrl) {
        evt.preventDefault();
        window.location.assign(collectionUrl);
      }
      return;
    }

    // El action del form se lee con getAttribute a proposito: `form.action` como
    // propiedad puede quedar secuestrado por un campo que se llame "action", igual
    // que pasa con form.id y <input name="id"> en la ficha de producto.
    const action = this.searchForm.getAttribute('action') || '/search';
    const url = new URL(action, window.location.origin);
    const term = this.getQuery();
    url.searchParams.set('q', term ? `${scope} AND ${term}` : scope);
    url.searchParams.set('options[prefix]', 'last');

    evt.preventDefault();
    window.location.assign(url.toString());
  }

  setupURL() {
    const url = new URL(`${window.shopUrl}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      // MAHA (2026-09-02): el value del selector YA es la expresion de busqueda
      // completa que arma predictive-search.liquid a partir del menu principal
      // -- p.ej. tag:"platos" o (tag:"Equipo menor" OR tag:"Equipo de cocina").
      // Antes aqui se anteponia product_type: al value; ahora seria doble prefijo.
      //
      // Se quita tambien el encodeURIComponent: url.searchParams.set() de la
      // linea siguiente YA codifica la cadena entera, asi que codificar antes la
      // codificaba dos veces. Medido el 2026-09-02 contra la tienda: con
      // categoria elegida, "plato hondo" viajaba como "plato%2520hondo" y
      // devolvia 0 resultados; sin el doble encode devuelve 10 de 10.
      search_term = `${this.searchProductTypes.value} AND ${search_term}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    const query = this.getQuery();
    if (query.length === 0) {
      this.clear();
      return;
    }
    if (query.length < this.constructor.MIN_QUERY_LENGTH) {
      this.abortController?.abort();
      this.removeAttribute('results');
      const target = document.getElementById('PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this));
      if (target) target.innerHTML = '';
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    this.abortController?.abort();
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    this.setLoadingState(true);

    fetch(url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.renderSearchResults(responseText);
        this.cachedMap.set(url, responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        // Una respuesta abortada (superada por una tecla más reciente) sigue
        // resolviendo su finally() de forma asíncrona; si para entonces ya hay
        // otro fetch en curso, no debe apagar SU estado "loading" ni marcar
        // "results" antes de tiempo. Solo actualiza si sigue siendo el fetch activo.
        if (this.abortController === controller) this.setLoadingState(false);
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = document.getElementById(id);

    if (targetElement) {
      const parser = new DOMParser();
      const parsedDoc = parser.parseFromString(responseText, 'text/html');
      const contentElement = parsedDoc.getElementById(id);

      if (contentElement) {
        this.searchContent?.classList.remove('hidden');
        targetElement.innerHTML = contentElement.innerHTML;

        if (this.isHeaderSearch && !document.body.classList.contains('search-open')) {
          document.body.classList.add('search-open');
          this.toggleNavButtonVisibility(true);
          this.classList.add('predictive-search-open');
        } else {
          this.classList.add('predictive-search-open');
        }
      } else {
        console.error(`Element with id '${id}' not found in the parsed response.`);
      }
    } else {
      console.error(`Element with id '${id}' not found in the document.`);
    }
  }

  handleScrollClose(event) {
    // MAHA (2026-08-25): coalescido con requestAnimationFrame -- ver la misma
    // nota en header.js DetailsDropdown.handleScrollClose. Como maximo un
    // cuadro de trabajo real por rafaga de scroll, con el evento mas reciente.
    this._scrollCloseEvent = event;
    if (this._scrollCloseRafId) return;
    this._scrollCloseRafId = requestAnimationFrame(() => {
      this._scrollCloseRafId = null;
      this.processScrollClose(this._scrollCloseEvent);
    });
  }

  processScrollClose(event) {
    if (!this.classList.contains(this.states.OPEN)) return;
    // Un scroll DENTRO del panel de resultados (lista larga con su propio
    // overflow) dispara su propio evento con ESE elemento como target, nunca
    // "document" -- eso es el cliente leyendo resultados, no cerrando nada.
    if (event.target !== document && this.contains(event.target)) return;
    if (event.target !== document) return;
    if (this.scrollYAtOpen === null) return;

    if (Math.abs(window.scrollY - this.scrollYAtOpen) > 8) {
      const hadFocusInside = this.contains(document.activeElement);
      this.toggleSearchState(false);
      if (hadFocusInside) {
        // de vuelta al campo de texto, nunca dentro del panel oculto
        this.input.blur();
      }
    }
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
      this.toggleNavButtonVisibility(!isOpen);
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton && this.toggleNavigationButton.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }

  // MAHA (2026-09-17): normalizacion para casar categorias EXACTAS -- misma
  // regla que sections/main-search.liquid (el aviso "buscabas esta categoria"),
  // a proposito duplicada en vez de compartida porque un lado es JS y el otro
  // Liquid; solo estos 5 acentos, nada de sinonimos ni coincidencia parcial.
  static normalizeSearchTerm(value) {
    const accents = { á: 'a', é: 'e', í: 'i', ó: 'o', ú: 'u' };
    return (value || '')
      .toLowerCase()
      .replace(/[áéíóú]/g, (ch) => accents[ch])
      .replace(/\s+/g, ' ')
      .trim();
  }

  static stripTrailingS(value) {
    return value.endsWith('s') ? value.slice(0, -1) : value;
  }

  // Busca en las <option> del propio <select> de categorias (mismo mapa que
  // predictive-search.liquid arma desde snippets/search-category-map.liquid)
  // una cuyo texto visible case EXACTO contra `term`. Devuelve su data-url o
  // null si ninguna casa.
  static matchCategoryUrl(term, selectEl) {
    if (!selectEl) return null;
    const termKey = PredictiveSearch.stripTrailingS(PredictiveSearch.normalizeSearchTerm(term));
    if (!termKey) return null;
    for (const option of selectEl.options) {
      if (!option.value) continue; // "Todos los productos"
      const labelKey = PredictiveSearch.stripTrailingS(PredictiveSearch.normalizeSearchTerm(option.textContent));
      if (labelKey && labelKey === termKey) {
        return option.dataset.url || null;
      }
    }
    return null;
  }
}
customElements.define('predictive-search', PredictiveSearch);
